import { CrossSectionCanvas } from '@pages/cross-section/shared/CrossSectionCanvas';
import Konva from 'konva';
import { getPipeElevationOffset } from '@shared/utils/PipeUtils';
import { RGBAColor } from '@shared/codelist/types';
import { ColorService } from '@shared/codelist/color-service';

function drawPipe(opts: { id: string; pipe: any; visible: boolean; csCanvas: CrossSectionCanvas; onClickCallback: (pipe: ICrossSectionVertex, x: number, y: number) => void }) {
    let { id, pipe, visible, csCanvas, onClickCallback } = opts;

    let point = pipe.point;
    let x = csCanvas.metersToScreenCoordsX(point.x);
    let y = csCanvas.metersToScreenCoordsY(point.y);
    let r = 5;
    let y_offset = 0;

    if (x > csCanvas.width - csCanvas.canvasOptions.side_margin / 2 || x < csCanvas.canvasOptions.side_margin / 2) {
        visible = false;
    }
    if (y < csCanvas.canvasOptions.top_margin || y > csCanvas.height - csCanvas.canvasOptions.bottom_margin) {
        visible = false;
    }

    let elevType = pipe.props['elevType'];
    let pipeNetType = pipe.props['pipeNetType'];

    if (isNumber(pipe.props['diameter'])) {
        r = (parseFloat(pipe.props['diameter']) * csCanvas.canvasOptions.x_scale) / 2;
    }
    let thicknessRaw = 0;
    if (isNumber(pipe.props['thickness'])) {
        thicknessRaw = parseFloat(pipe.props['thickness']);
    }
    const thickness = thicknessRaw * csCanvas.canvasOptions.y_scale;

    const diameter = pipe.props['diameter'] ? parseFloat(pipe.props['diameter']) : 0;
    y_offset = -getPipeElevationOffset(diameter, thicknessRaw, elevType, pipeNetType) * csCanvas.canvasOptions.y_scale;

    // Parse color from pipe color. This is used as a fallback if infraCoding is not available.
    const colorParts: string[] = pipe.color?.split(',') || ['0', '0', '0'];
    const colorAsRGBA: RGBAColor = { r: parseInt(colorParts[0], 10), g: parseInt(colorParts[1], 10), b: parseInt(colorParts[2], 10), a: 1 };

    // Fetch color based on the coding of the file. If infraCoding is not available, use the color from the response.
    const color = ColorService.getPipeColorFromInfraCode(pipe.props['infraCoding'], colorAsRGBA);
    const colorStr = color.r + ',' + color.g + ',' + color.b;

    let pipeCircle = new Konva.Circle({
        visible: visible,
        name: 'model_' + point.id,
        x: x,
        y: y + y_offset,
        stroke: getRgb(colorStr),
        radius: r,
    });

    pipeCircle.on('click tap', () => {
        onClickCallback(pipe, x, y);
        showTooltip(pipe, x, y, r, y_offset, true, csCanvas);
    });

    pipeCircle.on('mouseover', function () {
        showTooltip(pipe, x, y, r, y_offset, false, csCanvas);
    });

    pipeCircle.on('mouseout', csCanvas.clearHoverTooltips);

    // outer edge of the pipe.
    if (thickness > 0) {
        let outerPipeCircle = new Konva.Circle({
            visible: visible,
            name: 'model_' + point.id,
            x: x,
            y: y + y_offset,
            stroke: getRgb(colorStr),
            radius: r + thickness,
        });
        csCanvas.contentLayer.add(outerPipeCircle);
    }

    csCanvas.contentLayer.add(pipeCircle);

    return [pipeCircle];
}

const showTooltip = (pipe: any, x: number, y: number, r: number, y_offset: number, showProps: boolean, csCanvas: CrossSectionCanvas) => {
    let size = r * 2 + 4;
    let t: string[] = [
        pipe.name,
        kuura.getMessage('js.cross_section.b') + ': ' + pipe.point.x.toFixed(2),
        kuura.getMessage('js.cross_section.z') + ': ' + pipe.point.y.toFixed(2),
    ].filter(Boolean);

    if (showProps) {
        if (Object.keys(t).length > 0) {
            t.push('\n');
        }
        Object.entries(pipe.props).forEach(([key, val]) => {
            t.push(key + ': ' + val);
        });
    }

    const centerY = y + y_offset;

    let bbRect = new Konva.Rect({
        x: x - (size - 1) / 2,
        y: centerY - (size - 1) / 2,
        width: size,
        height: size,
        stroke: 'gray',
        strokeWidth: 1,
        listening: false,
    });

    csCanvas.tooltipLayer.add(bbRect);
    csCanvas.drawTooltip(x, centerY - r - 14, t.join('\n'), !!showProps);
    csCanvas.drawSnapCursor(true, x, centerY);
};

const getRgb = (color: string) => 'rgb(' + color + ')';

const isNumber = (n: any) => !isNaN(parseFloat(n)) && isFinite(n);

export const CrossSectionPipe = {
    drawPipe,
};
